import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Select,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  Text,
  useToast,
  ButtonGroup,
  Flex,
} from '@chakra-ui/react'
import { useCallback, useState, useEffect, useRef } from 'react'
import { useDropzone } from 'react-dropzone'
import { useAgentStore, AIParadigm } from '../../stores/agentStore'

interface AgentDialogProps {
  isOpen: boolean
  onClose: () => void
  selectedAgentId?: number
}

interface FormData {
  name: string
  paradigm: AIParadigm | ''
  model: string
  role: string
  task: string
  dataSource: string
  constraints: string
}

export const AgentDialog = ({ isOpen, onClose, selectedAgentId }: AgentDialogProps) => {
  const { agents, addAgent, updateAgent, deleteAgent } = useAgentStore()
  const selectedAgent = selectedAgentId ? agents.find(a => a.id === selectedAgentId) : undefined
  const toast = useToast()
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false)
  const cancelRef = useRef<HTMLButtonElement>(null)

  const [formData, setFormData] = useState<FormData>({
    name: '',
    paradigm: '',
    model: '',
    role: '',
    task: '',
    dataSource: '',
    constraints: '',
  })

  useEffect(() => {
    if (selectedAgent) {
      setFormData({
        name: selectedAgent.name,
        paradigm: selectedAgent.paradigm,
        model: selectedAgent.model || '',
        role: selectedAgent.role || '',
        task: selectedAgent.task || '',
        dataSource: selectedAgent.dataSource || '',
        constraints: selectedAgent.constraints || '',
      })
    } else {
      setFormData({
        name: '',
        paradigm: '',
        model: '',
        role: '',
        task: '',
        dataSource: '',
        constraints: '',
      })
    }
  }, [isOpen, selectedAgent])

  const isNameUnique = (name: string) => {
    return !agents.some(agent => 
      agent.name.toLowerCase() === name.toLowerCase() && 
      agent.id !== selectedAgentId
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      toast({
        title: "Name is required",
        status: "error",
        duration: 3000,
      })
      return
    }

    if (!isNameUnique(formData.name)) {
      toast({
        title: "Agent name must be unique",
        status: "error",
        duration: 3000,
      })
      return
    }

    if (formData.paradigm === '') {
      toast({
        title: "Please select a paradigm",
        status: "error",
        duration: 3000,
      })
      return
    }

    const validFormData = {
      ...formData,
      paradigm: formData.paradigm as AIParadigm
    }

    if (selectedAgentId) {
      updateAgent(selectedAgentId, validFormData)
    } else {
      addAgent({ ...validFormData, status: 'active' })
    }
    onClose()
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Handle file upload logic here
    console.log(acceptedFiles)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

  const handleDelete = () => {
    if (selectedAgentId) {
      deleteAgent(selectedAgentId)
      setIsDeleteAlertOpen(false)
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent position="relative">
        <ModalHeader>
          {selectedAgentId ? 'Edit Agent' : 'Create New Agent'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {isDeleteAlertOpen ? (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg="blackAlpha.400"
              zIndex={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box
                bg="white"
                p={6}
                borderRadius="md"
                boxShadow="lg"
                maxW="90%"
                width="400px"
              >
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  Delete Agent
                </Text>
                
                <Text mb={6}>
                  Are you sure you want to delete this agent?
                  <Text mt={2}>
                    This action cannot be undone.
                  </Text>
                </Text>

                <Flex justifyContent="flex-end" gap={3}>
                  <Button 
                    ref={cancelRef} 
                    onClick={() => setIsDeleteAlertOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    colorScheme="red" 
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                </Flex>
              </Box>
            </Box>
          ) : null}

          <form onSubmit={handleSubmit}>
            <Grid templateColumns="1fr" gap={4} mb={4}>
              <GridItem>
                <FormControl isRequired>
                  <FormLabel>Agent Name</FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter agent name"
                    maxLength={30}
                  />
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    {formData.name.length}/30 characters
                  </Text>
                </FormControl>
              </GridItem>
            </Grid>

            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <FormControl isRequired>
                  <FormLabel>AI Paradigm</FormLabel>
                  <Select
                    name="paradigm"
                    value={formData.paradigm}
                    onChange={handleChange}
                    placeholder="Select paradigm"
                  >
                    <option value="nlp">Natural Language Processing (NLP)</option>
                    <option value="cv">Computer Vision (CV)</option>
                    <option value="ts">Time Series Forecasting & Regression</option>
                    <option value="anomaly">Anomaly Detection</option>
                    <option value="rl">Reinforcement Learning (RL)</option>
                    <option value="generative">Simulation and Generative Modelling</option>
                  </Select>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Model</FormLabel>
                  <Select
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    placeholder="Select model"
                  >
                    <option value="llama2">Llama 2</option>
                    <option value="mistral">Mistral</option>
                    <option value="gpt4all">GPT4All</option>
                  </Select>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Role</FormLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    placeholder="Select role"
                  >
                    <option value="monitor">Monitor</option>
                    <option value="planner">Planner</option>
                    <option value="simulator">Simulator</option>
                    <option value="integrator">Integrator</option>
                    <option value="decision-maker">Decision-Maker</option>
                  </Select>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Task</FormLabel>
                  <Select
                    name="task"
                    value={formData.task}
                    onChange={handleChange}
                    placeholder="Select task"
                  >
                    <option value="monitoring">System Monitoring</option>
                    <option value="analysis">Data Analysis</option>
                    <option value="prediction">Predictive Maintenance</option>
                  </Select>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Data Source</FormLabel>
                  <Select
                    name="dataSource"
                    value={formData.dataSource}
                    onChange={handleChange}
                    placeholder="Select data source"
                  >
                    <option value="dt1">Digital Twin 1</option>
                    <option value="dt2">Digital Twin 2</option>
                    <option value="system">System Data</option>
                  </Select>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Constraints</FormLabel>
                  <Input
                    name="constraints"
                    value={formData.constraints}
                    onChange={handleChange}
                    placeholder="Enter constraints"
                  />
                </FormControl>
              </GridItem>

              <GridItem colSpan={2}>
                <Box {...getRootProps()} p={4} border="2px" borderStyle="dashed">
                  <input {...getInputProps()} />
                  <Text>
                    {isDragActive
                      ? "Drop the files here..."
                      : "Drag 'n' drop scripts here, or click to select files"}
                  </Text>
                </Box>
              </GridItem>

              <GridItem colSpan={2}>
                {selectedAgentId ? (
                  <ButtonGroup width="full" spacing={4}>
                    <Button 
                      colorScheme="red" 
                      variant="outline"
                      onClick={() => setIsDeleteAlertOpen(true)}
                      width="30%"
                    >
                      Delete Agent
                    </Button>
                    <Button 
                      type="submit" 
                      colorScheme="blue"
                      width="70%"
                    >
                      Update Agent
                    </Button>
                  </ButtonGroup>
                ) : (
                  <Button type="submit" colorScheme="blue" width="full">
                    Deploy Agent
                  </Button>
                )}
              </GridItem>
            </Grid>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
} 